import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BriefcaseBusiness, Plus, UsersRound } from "lucide-react";
import { toast } from "sonner";

import { api, getErrorMessage } from "@/api";

const initialForm = {
  title: "", event_name: "", location: "", category: "", event_date: "", budget: "", description: "",
};

export default function ManagerDashboard() {
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [plan, setPlan] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchJob, setMatchJob] = useState(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState("");
  const [form, setForm] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);

  const loadData = async () => {
    try {
      const [jobsResult, applicationsResult, planResult] = await Promise.all([
        api.get("/jobs/mine"),
        api.get("/applications"),
        api.get("/billing/plan"),
      ]);
      setJobs(jobsResult.data);
      setApplications(applicationsResult.data);
      setPlan(planResult.data);
      setMatchJob((current) => {
        if (current && jobsResult.data.some((job) => job.id === current.id)) return current;
        return jobsResult.data[0] || null;
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const loadMatches = async () => {
      if (!matchJob) return;
      setMatchesLoading(true);
      setMatchesError("");
      try {
        const { data } = await api.get(`/jobs/${matchJob.id}/matches`);
        setMatches(data);
      } catch (error) {
        setMatchesError(getErrorMessage(error));
        toast.error(getErrorMessage(error));
      } finally {
        setMatchesLoading(false);
      }
    };
    loadMatches();
  }, [matchJob]);

  const createJob = async (event) => {
    event.preventDefault();
    try {
      await api.post("/jobs", { ...form, budget: Number(form.budget) });
      toast.success("Opportunity published.");
      setForm(initialForm);
      setShowForm(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}`, { status });
      toast.success(`Candidate ${status}.`);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <main className="dashboard-page" data-testid="manager-dashboard-page">
      <header className="dashboard-heading">
        <div>
          <p className="eyebrow">MANAGER WORKSPACE</p>
          <h1>Build the room<br /><em>behind the moment.</em></h1>
        </div>
        <button className="primary-button" data-testid="create-job-button" onClick={() => setShowForm(!showForm)}>
          <Plus size={17} /> Post an opportunity
        </button>
      </header>
      {plan && <section className="launch-plan" data-testid="launch-plan-panel">
        <div>
          <p className="eyebrow">{plan.plan_name}</p>
          <h2 data-testid="launch-plan-title">Three posts each month — on us.</h2>
          <p data-testid="launch-plan-detail">
            {plan.posts_remaining} of {plan.monthly_post_limit} posts remain this month.
          </p>
        </div>
        <span data-testid="launch-plan-future-price">
          Future plan: ₹{plan.future_monthly_price}/month · extra posts ₹{plan.future_extra_post_price}
        </span>
      </section>}
      {showForm && (
        <form className="job-form" data-testid="create-job-form" onSubmit={createJob}>
          {Object.entries(form).map(([key, value]) => (
            <label data-testid={`job-form-${key}-label`} key={key}>
              {key.replace("_", " ")}
              {key === "description" ? (
                <textarea data-testid={`job-form-${key}-input`} name={key} onChange={(e) => setForm({ ...form, [key]: e.target.value })} required value={value} />
              ) : (
                <input data-testid={`job-form-${key}-input`} name={key} onChange={(e) => setForm({ ...form, [key]: e.target.value })} required value={value} />
              )}
            </label>
          ))}
          <button className="primary-button" data-testid="job-form-submit-button">Publish</button>
        </form>
      )}
      <section className="dashboard-grid">
        <div className="workspace-panel" data-testid="manager-jobs-panel">
          <div className="panel-title"><BriefcaseBusiness size={18} /><h2>Your opportunities</h2></div>
          {jobs.map((job) => <div className={`workspace-row ${matchJob?.id === job.id ? "selected" : ""}`} data-testid={`manager-job-${job.id}`} key={job.id}>
            <div><strong data-testid={`manager-job-title-${job.id}`}>{job.title}</strong><span>{job.event_name}</span></div>
            <div className="job-row-actions">
              <span className="count-chip" data-testid={`manager-job-count-${job.id}`}>{job.applicant_count} applicants</span>
              <button data-testid={`view-matches-${job.id}`} onClick={() => setMatchJob(job)}>Matches</button>
            </div>
          </div>)}
        </div>
        <div className="workspace-panel applicants-panel" data-testid="manager-applications-panel">
          <div className="panel-title"><UsersRound size={18} /><h2>Talent pipeline</h2></div>
          {applications.map((application) => <div className="candidate-row" data-testid={`candidate-${application.id}`} key={application.id}>
            <div><strong data-testid={`candidate-name-${application.id}`}>{application.freelancer_name}</strong><span>{application.job_title}</span><p>{application.cover_letter}</p></div>
            <div className="candidate-actions">
              <span className={`status-tag ${application.status}`} data-testid={`candidate-status-${application.id}`}>{application.status}</span>
              <button data-testid={`candidate-shortlist-${application.id}`} onClick={() => setStatus(application.id, "shortlisted")}>Shortlist</button>
              <button data-testid={`candidate-hire-${application.id}`} onClick={() => setStatus(application.id, "hired")}>Hire</button>
            </div>
          </div>)}
        </div>
      </section>
      {matchJob && <section className="matches-panel" data-testid="talent-matches-panel">
        <div className="matches-heading">
          <div><p className="eyebrow">JOB-ALIGNED TALENT</p><h2 data-testid="matches-job-title">Matches for {matchJob.title}</h2></div>
          <span data-testid="matches-count">{matches.length} recommended profiles</span>
        </div>
        <div className="matches-grid">
          {matchesLoading && <p data-testid="matches-loading">Loading recommendations…</p>}
          {matchesError && <p data-testid="matches-error">{matchesError}</p>}
          {!matchesLoading && !matchesError && matches.map((person) => <article data-testid={`talent-match-${person.id}`} key={person.id}>
            <div><span className="match-score" data-testid={`match-score-${person.id}`}>{person.match_score}% match</span><h3>{person.name}</h3><p>{person.portfolio?.headline}</p></div>
            <p className="match-reason" data-testid={`match-reason-${person.id}`}>{person.match_reason}</p>
            <Link data-testid={`message-match-${person.id}`} to={`/messages?with=${person.id}`}>Message</Link>
          </article>)}
        </div>
      </section>}
    </main>
  );
}